import {useCallback, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {getCheckAuthenticatorToken, logoutAuth, validateOtp} from '../core/_requests'
import {useAuth} from '../core/Auth'
import clsx from 'clsx'
import {GoogleReCaptcha, GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {intervalRefreshRecaptcha} from '../../../../_metronic/helpers'
import {OtpRequest, setUser} from '../core/AuthHelpers'
import {useNavigate} from 'react-router-dom'
import LoadingButtonWrapper from '../../../components/LoadingButtonWrapper'
import QRCode from 'react-qr-code'
import {parseJsonAndPascalToCamel} from '../../../utils/array'

const isUseCaptcha = process.env.REACT_APP_USE_CAPTCHA === 'true'
const captchaKey = process.env.REACT_APP_CAPTCHA_TOKEN ?? ''

export function Otp() {
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [refreshCaptcha, setRefreshCaptcha] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [authToken, setAuthToken] = useState<string>('')
  const [userInfo, setUserInfo] = useState<any | null>(null)
  const [isLogoutLoading, setLogoutLoading] = useState<boolean>(false)

  const navigate = useNavigate()

  useEffect(() => {
    const data = getOtp()

    if (data === null) {
      navigate('/auth')
    }

    const intervalId = setInterval(() => {
      setRefreshCaptcha((r) => !r)
    }, intervalRefreshRecaptcha)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    const localStorageValue = getOtp()

    if (localStorageValue === null) return

    const req: OtpRequest = {
      guidToken: localStorageValue.guid,
      userId: localStorageValue.userId,
      otp: '',
      captchaToken: isUseCaptcha ? captchaToken : '',
    }

    getCheckAuthenticatorToken(req).then(({data}) => {
      setAuthToken(data.uri)
      setUserInfo({
        name: data.name,
        loginName: data.loginName,
      })
    })
  }, [])

  const {getOtp, saveAuth, setCurrentUser, logout} = useAuth()

  const onVerify = useCallback((token: string) => {
    setCaptchaToken(token)
  }, [])

  const formik = useFormik({
    initialValues: {otp: ''},
    validationSchema: Yup.object().shape({
      otp: Yup.string()
        .min(1, 'OTP Minimal 1 characters')
        .max(24, 'OTP Maximal 24 characters')
        .required('OTP is required'),
    }),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        setLoading(true)
        setStatus(undefined)

        const localStorageValue = getOtp()

        if (localStorageValue === null) return

        const req: OtpRequest = {
          guidToken: localStorageValue.guid,
          userId: localStorageValue.userId,
          otp: values.otp,
          captchaToken: isUseCaptcha ? captchaToken : '',
        }

        const {data, message} = await validateOtp(req)
        console.log(data)

        if (data) {
          const {token, userInfo, guid} = data

          const currentUser = {
            username: userInfo.loginName,
            email: userInfo.additionalInfo.email,
            majorName: userInfo.additionalInfo.studentMajorName,
            degreeName: userInfo.additionalInfo.studentMajorDegreeName,
            nim: userInfo.additionalInfo.studentMajorNim,
            id: userInfo.id,
            name: userInfo.name,
            // roleId: userInfo.roles[0]?.id,
            // roleName: userInfo.roles[0]?.name,
            roleId: 0,
            roleName: 'test',
            limitFileSize: userInfo.additionalInfo.limitFileSize,
            limitFileSizeMb: userInfo.additionalInfo.limitFileSize / 1048576,
            staffMajors: userInfo.additionalInfo.staffMajorList
              ? parseJsonAndPascalToCamel(userInfo.additionalInfo.staffMajorList)
              : null,
            levelId: userInfo.additionalInfo.levelId,
          }

          // setCurrentUser(data.userInfo)
          // setUser(data.userInfo)
          // saveAuth({api_token: data.token})

          setCurrentUser(currentUser)

          saveAuth({api_token: token})
          setUser(currentUser)
        } else {
          setStatus(message)
        }
        setLoading(false)
      } catch (error: any) {
        setStatus(error.message)
        setSubmitting(false)
        setLoading(false)
        setRefreshCaptcha(!refreshCaptcha)
        formik.setFieldValue('otp', '')
      }
    },
  })

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className='text-dark fw-bolder mb-3 fs-3'>{userInfo?.name}</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            Input OTP code from Authenticator App
          </div>
        </div>
        {/* begin::Heading */}

        {formik.status ? (
          <div className='mb-lg-5 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : (
          ''
        )}

        {/* begin::Form group */}
        {authToken !== '' && (
          <div className='fv-row mb-8 text-center'>
            <QRCode value={authToken} size={150} />
          </div>
        )}

        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-dark'>OTP</label>
          <input
            placeholder='OTP'
            {...formik.getFieldProps('otp')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.otp && formik.errors.otp},
              {
                'is-valid': formik.touched.otp && !formik.errors.otp,
              }
            )}
            type='text'
            name='otp'
            autoComplete='off'
          />
          {formik.touched.otp && formik.errors.otp && (
            <div className='fv-plugins-message-container'>
              <span role='alert'>{formik.errors.otp}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Action */}
        <div className='d-grid mb-5'>
          <LoadingButtonWrapper isLoading={loading}>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              <span className='indicator-label'>Validate</span>
            </button>
          </LoadingButtonWrapper>
        </div>
        <div className='d-grid'>
          <LoadingButtonWrapper isLoading={loading}>
            <button
              type='button'
              id='kt_sign_in_back'
              className='btn btn-outline btn-outline-danger'
              disabled={formik.isSubmitting || isLogoutLoading}
              onClick={() => {
                setLogoutLoading(true)
                logout()
                navigate('/')
                setLogoutLoading(false)
              }}
            >
              <span className='indicator-label'>Kembali</span>
            </button>
          </LoadingButtonWrapper>
        </div>
        {/* end::Action */}
      </form>
      {isUseCaptcha && (
        <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
          <GoogleReCaptcha
            onVerify={onVerify}
            refreshReCaptcha={refreshCaptcha}
            action='validate_otp'
          />
        </GoogleReCaptchaProvider>
      )}
    </>
  )
}
